import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/api';
import { useAuth } from './AuthContext';

const PopupContext = createContext();

export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);

  const { authState } = useAuth();
  const user = authState?.user;

  const markPopupAsShown = useCallback((popupId) => {
    if (popupId) {
      sessionStorage.setItem(`popupShown_${popupId}`, 'true');
      setHasShownThisSession(true);
    }
  }, []);

  useEffect(() => {
    const fetchActivePopup = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get('/api/popups/active');
        
        const activePopups = response.data || [];
        
        const eligiblePopups = activePopups.filter(p => {
          if (p.target_audience === 'all') return true;
          if (p.target_audience === 'logged_in' && user) return true;
          if (p.target_audience === 'logged_out' && !user) return true;
          return false;
        });

        // Sort eligible popups by priority: logged_in > logged_out > all
        // And then by updated_at (most recent first)
        eligiblePopups.sort((a, b) => {
          const getPriority = (popup) => {
            // Logged-in user specific popups get highest priority
            if (popup.target_audience === 'logged_in' && user) return 3;
            // Logged-out user specific popups get medium priority
            if (popup.target_audience === 'logged_out' && !user) return 2;
            // General "all" popups get lowest priority as fallback
            if (popup.target_audience === 'all') return 1;
            return 0; // Should not happen for eligible popups
          };

          const priorityA = getPriority(a);
          const priorityB = getPriority(b);

          if (priorityA !== priorityB) {
            return priorityB - priorityA; // Higher priority first
          }

          // If priorities are the same, sort by most recent update
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });
        
        setPopup(eligiblePopups.length > 0 ? eligiblePopups[0] : null);
      } catch (err) {
        console.error('Failed to fetch active popup:', err);
        setError(err);
        setPopup(null);
      } finally {
        setLoading(false);
      }
    };

    fetchActivePopup();
  }, [user]);

  useEffect(() => {
    // Check sessionStorage on mount or when popup changes
    if (popup && sessionStorage.getItem(`popupShown_${popup.id}`) === 'true') {
      setHasShownThisSession(true);
    } else {
      setHasShownThisSession(false);
    }
  }, [popup]); // Re-check when popup changes

  const contextValue = {
    popup,
    loading,
    error,
    hasShownThisSession, // Use the state variable
    markPopupAsShown,
  };

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};
